import React from 'react';
import { Link } from 'react-router-dom';

interface SmartLinkProps {
  to?: string;
  href?: string;
  target?: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
}

/**
 * 智能链接组件
 * 自动判断是内部路由链接(React Router)还是外部链接(a标签)
 * 支持锚点链接的平滑滚动
 *
 * @param to - 内部路由路径 (如 '/solution', '/solution#features')
 * @param href - 外部链接URL (如 'https://example.com')
 * @param target - 链接打开方式 (如 '_blank')
 * @param className - CSS类名
 * @param children - 子元素
 * @param onClick - 点击事件处理函数
 */
export const SmartLink: React.FC<SmartLinkProps> = ({ to, href, target, className, style, children, onClick }) => {
  // 处理锚点链接的平滑滚动
  const handleHashClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      onClick(e);
      return;
    }

    if (to?.includes('#')) {
      const [path, hash] = to.split('#');
      // 如果是当前页面的锚点
      if (!path || path === window.location.pathname) {
        e.preventDefault();
        const element = document.getElementById(hash);
        if (element) {
          const headerHeight = 80; // Header高度
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerHeight;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }
    }
  };

  // 优先使用to (内部路由)
  if (to) {
    // 纯锚点链接
    if (to.startsWith('#')) {
      return (
        <a href={to} className={className} style={style} onClick={handleHashClick}>
          {children}
        </a>
      );
    }

    // 内部路由链接
    return (
      <Link to={to} className={className} style={style} onClick={handleHashClick}>
        {children}
      </Link>
    );
  }

  // 外部链接
  return (
    <a
      href={href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      className={className}
      style={style}
      onClick={onClick}
    >
      {children}
    </a>
  );
};

export default SmartLink;
